import { ArrowRight, Check } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND, STATS } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionHeading,
  SectionWrap,
  FadeUp,
  CtaBand,
} from "@/components/sections/Primitives";
import ContactForm from "@/components/forms/ContactForm";

export default function FranchisePage() {
  useSeo({
    title: "Franchise Opportunity | Pizza Excellente",
    description:
      "Explore the Pizza Excellente franchise opportunity in India, including brand positioning, support, operating model, and long-term growth potential.",
    path: "/franchise-opportunity",
    ogImage: ASSETS.cafe_interior,
    ogImageAlt: "Pizza Excellente franchise opportunity",
  });

  const support = [
    "Residential site evaluation and location guidance",
    "Store layout and kitchen workflow planning",
    "Initial training and pre-opening support",
    "SOPs, recipe documentation, and menu engineering",
    "POS and operational system support (Petpooja-ready)",
    "Local marketing and launch guidance",
    "Ongoing operational support and audits",
    "Supply chain guidance for key ingredients and recipes",
  ];

  return (
    <div data-testid="franchise-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-48 pb-16 md:pb-24 bg-pe-ink text-pe-cream overflow-hidden">
        <img
          src={ASSETS.cafe_interior}
          alt="Pizza Excellente franchise store interior concept"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-pe-ink/70 via-pe-ink/85 to-pe-ink" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-4xl">
            <SectionEyebrow>
              <span className="text-pe-orange">Franchise Opportunity</span>
            </SectionEyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-cream mt-6 leading-[1.02]">
              Partner with a neighborhood-focused pizza brand <span className="italic text-pe-rose-soft">built for scale.</span>
            </h1>
            <p className="mt-8 text-pe-cream/75 text-lg leading-relaxed max-w-2xl">
              Pizza Excellente offers franchise partners a differentiated concept, a clear operating model, and support designed for disciplined local growth.
            </p>
            <a
              data-testid="franchise-hero-cta"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-10 bg-pe-orange text-pe-ink hover:bg-white transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
            >
              Enquire About Franchising <ArrowRight size={16} />
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-8">
            {[STATS.fee, STATS.royalty, STATS.term, STATS.investment, STATS.payback].map((s) => (
              <div key={s.label} className="border-t border-white/15 pt-5">
                <div className="font-serif text-3xl md:text-4xl text-pe-orange leading-none">{s.value}</div>
                <div className="mt-3 text-[10px] tracking-[0.18em] uppercase text-pe-cream/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 1 — Why franchise */}
      <SectionWrap testId="franchise-why" tone="cream">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <FadeUp className="md:col-span-5">
            <SectionHeading className="mt-5">Why franchise with us.</SectionHeading>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7 space-y-6 text-pe-ink/75 text-lg leading-relaxed">
            <p>
              Pizza Excellente combines authentic product positioning, a compact and efficient QSR format, and a strategy
              built around high-density residential demand.
            </p>
            <p>
              The model is designed to create recurring neighborhood business, lower operating friction, and strong long-term brand relevance —
              all while preserving consistency in customer experience and product quality.
            </p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 2 — Who we look for */}
      <SectionWrap testId="franchise-who" tone="mauve">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <FadeUp className="md:col-span-6">
            <SectionEyebrow>Ideal partner</SectionEyebrow>
            <SectionHeading className="mt-5">Who we’re looking for.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              The ideal franchise partner has strong community ties, local market knowledge, and a commitment to disciplined execution.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed">
              The strongest operators value quality, customer service, consistency, and long-term brand building.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-px bg-pe-ink/10">
              {[
                "Community ties",
                "Local market insight",
                "Disciplined execution",
                "Quality commitment",
                "Customer service mindset",
                "Long-term builders",
              ].map((trait) => (
                <div key={trait} className="bg-pe-cream p-6 md:p-7">
                  <div className="font-serif text-2xl text-pe-maroon leading-tight">{trait}</div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 3 — Support */}
      <SectionWrap testId="franchise-support" tone="white">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>What you receive</SectionEyebrow>
            <SectionHeading className="mt-5">Support across the journey.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              Franchisees receive support across site evaluation, kitchen setup, training, operational systems, local marketing,
              ongoing field support, and supply chain coordination.
            </p>
            <p className="mt-5 text-pe-ink/75 text-lg leading-relaxed">
              The goal: help each franchisee launch with speed, consistency, and a clear path to operational stability.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {support.map((s, i) => (
                <li key={s} data-testid={`support-bullet-${i}`} className="flex items-start gap-3 border-b border-pe-ink/10 pb-5">
                  <Check className="text-pe-maroon shrink-0 mt-1" size={18} strokeWidth={2} />
                  <span className="text-pe-ink/80 leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Section 4 — Growth model */}
      <SectionWrap testId="franchise-growth" tone="ink">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-6">
            <SectionEyebrow>
              <span className="text-pe-orange">Growth model</span>
            </SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-pe-cream mt-5 leading-[1.05]">
              Company-owned + franchised, with master options.
            </h2>
            <p className="mt-8 text-pe-cream/75 text-lg leading-relaxed">
              Pizza Excellente plans to expand through both company-owned stores and franchised units. In selected regions,
              master franchise structures may also be considered to support market development while maintaining brand standards.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-px bg-white/10">
              <div className="bg-pe-ink p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">5</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">Company-owned (4 yrs)</div>
              </div>
              <div className="bg-pe-ink p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">30</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">Franchised units</div>
              </div>
              <div className="bg-pe-ink p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">T2/T3</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">India cities focus</div>
              </div>
              <div className="bg-pe-ink p-7">
                <div className="font-serif text-5xl text-pe-orange leading-none">Master</div>
                <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-cream/70">Optional structure</div>
              </div>
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* Inline short franchise enquiry form */}
      <SectionWrap testId="franchise-inline-form" tone="cream">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>Short enquiry</SectionEyebrow>
            <SectionHeading className="mt-5">Start the franchise conversation.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              Tell us a little about yourself and the city you’re considering. Our team reviews every serious enquiry and responds personally.
            </p>
            <a
              data-testid="franchise-mailto-link"
              href={BRAND.mailto}
              className="inline-flex items-center gap-2 mt-8 text-pe-maroon hover:text-pe-maroon-deep text-sm tracking-[0.15em] uppercase font-medium"
            >
              Email for Franchise Details <ArrowRight size={16} />
            </a>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="bg-white border border-pe-border p-6 md:p-10">
              <ContactForm
                compact
                defaultEnquiryType="franchise"
                sourcePage="franchise-opportunity"
                testIdPrefix="franchise-form"
              />
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      <CtaBand
        testId="franchise-cta-band"
        eyebrow="Next step"
        heading="Build the neighborhood pizza brand of tomorrow."
        body="If you're looking to partner with a pizza brand that combines product authenticity, neighborhood relevance, and a scalable operating model — Pizza Excellente is built for the next stage of India's QSR growth."
        ctaLabel="Email for Franchise Details"
        ctaHref={BRAND.mailto}
        secondaryLabel="View Investment Overview"
        secondaryHref="/investment"
      />
    </div>
  );
}
